import { cn } from '@rocco/utils/cn';
import { ReactComponent as ChevronDown } from '@rocco/icons/svg/arrow/arrow-chevron-down.svg';

export interface CarouselPaginationPillProps {
    /** Number of slides. The pill is hidden when there is one or fewer. */
    total: number;
    /** Index of the active slide. */
    activeIndex: number;
    onPrev: () => void;
    onNext: () => void;
    /** Jump straight to a slide by index (dot click). */
    onSelect: (index: number) => void;
    /** Accessible label for the control group, e.g. "Projects carousel". */
    label: string;
    /** `light` = dark pill on light sections; `dark` = light pill on dark sections. */
    theme?: 'light' | 'dark';
    className?: string;
}

/**
 * Rounded pill with prev/next chevrons either side of a row of dots — the
 * pagination control used across the homepage carousels (feature strip,
 * projects, professionals). Generalised from the B2B testimonial pill but
 * sized on the standard rem scale so it renders consistently outside the
 * B2B 1920-coord frame. Controlled: the parent owns `activeIndex`.
 */
export const CarouselPaginationPill = ({
    total,
    activeIndex,
    onPrev,
    onNext,
    onSelect,
    label,
    theme = 'light',
    className,
}: CarouselPaginationPillProps) => {
    if (total <= 1) return null;

    const isDark = theme === 'dark';
    const pillClass = isDark
        ? 'border-white/30 bg-transparent'
        : 'border-gray-300 bg-white';
    // The chevron svg has no fill attribute (defaults to black) and does not
    // use currentColor, so `text-*` never reaches it — set `fill-*` directly.
    const chevronClass = isDark ? 'fill-white' : 'fill-gray-900';
    const activeDotClass = isDark ? 'bg-white' : 'bg-gray-900';
    const inactiveDotClass = isDark
        ? 'bg-white/40 hover:bg-white/70'
        : 'bg-gray-300 hover:bg-gray-500';

    return (
        <div
            role="group"
            aria-label={label}
            className={cn(
                'inline-flex items-center gap-1 rounded-full border px-2 py-1.5',
                pillClass,
                className,
            )}
        >
            <button
                type="button"
                onClick={onPrev}
                aria-label="Previous slide"
                className="flex size-4.5 cursor-pointer items-center justify-center transition-opacity hover:opacity-60"
            >
                <ChevronDown
                    className={cn('size-3.5 rotate-90', chevronClass)}
                />
            </button>
            <div className="flex items-center gap-1.5 px-1">
                {Array.from({ length: total }, (_, index) => (
                    <button
                        key={`dot-${index}`}
                        type="button"
                        onClick={() => onSelect(index)}
                        aria-label={`Go to slide ${index + 1}`}
                        aria-current={index === activeIndex}
                        className={cn(
                            'block size-2 cursor-pointer rounded-full transition-colors duration-200',
                            index === activeIndex
                                ? activeDotClass
                                : inactiveDotClass,
                        )}
                    />
                ))}
            </div>
            <button
                type="button"
                onClick={onNext}
                aria-label="Next slide"
                className="flex size-4.5 cursor-pointer items-center justify-center transition-opacity hover:opacity-60"
            >
                <ChevronDown
                    className={cn('size-3.5 -rotate-90', chevronClass)}
                />
            </button>
        </div>
    );
};
